import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

/**
 * Toast notification system.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.show("Content generation started", { kind: "success" });
 *   toast.show("Generation failed", { kind: "error" });
 *
 * The provider must wrap any component tree that calls useToast().
 * Toasts auto-dismiss after 4s by default. They stack vertically in the
 * top-right corner.
 */

export type ToastKind = "info" | "success" | "warning" | "error";

export interface ToastOptions {
  kind?: ToastKind;
  durationMs?: number;
}

interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
  expiresAt: number;
}

interface ToastApi {
  show: (message: string, opts?: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const show = useCallback((message: string, opts: ToastOptions = {}) => {
    const id = ++nextId;
    // Default durations:
    //   error:   12s (longest — user needs time to read stack traces)
    //   warning: 10s (multi-line diagnostic info often included)
    //   success: 10s (multi-line "filled X fields" summaries)
    //   info:    10s (consistent with the others; no reason to be shorter)
    // Each kind can override via durationMs. Toasts are also dismissable
    // via the × button regardless of duration.
    const defaultMs = opts.kind === "error" ? 12000 : 10000;
    const durationMs = opts.durationMs ?? defaultMs;
    // chunk3z-fix486: a sticky toast (durationMs === 0) never auto-dismisses —
    // it stays until the user hits ×. Used for the scan summary, which carries
    // numbers the user needs time to read.
    const expiresAt = durationMs === 0 ? Infinity : Date.now() + durationMs;
    setToasts((prev) => [...prev, { id, message, kind: opts.kind ?? "info", expiresAt }]);
    if (durationMs > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, durationMs);
    }
  }, []);

  const api: ToastApi = { show };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Defensive default: log to console if toast provider missing.
    return { show: (msg) => console.log("[toast]", msg) };
  }
  return ctx;
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  // Animate-in via CSS transition on mount.
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <ToastTile key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastTile({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(id);
  }, []);

  const styles = TOAST_STYLES[toast.kind];

  return (
    <div
      className={`pointer-events-auto flex items-start gap-2 ${styles.bg} ${styles.border} border rounded-md shadow-card px-4 py-2.5 min-w-[280px] max-w-lg transition-all duration-200 ${
        mounted ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
      }`}
    >
      <span className={styles.iconColor + " text-base mt-0.5"}>{styles.icon}</span>
      <span className="text-text text-sm flex-1 whitespace-pre-line break-words leading-snug">
        {toast.message}
      </span>
      <button
        type="button"
        onClick={onDismiss}
        className="text-text-muted hover:text-text text-lg leading-none mt-0.5 px-1 -mr-1 rounded hover:bg-bg-subtle transition-colors"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

const TOAST_STYLES: Record<
  ToastKind,
  { bg: string; border: string; icon: string; iconColor: string }
> = {
  info: {
    bg: "bg-bg-card",
    border: "border-border",
    icon: "ℹ",
    iconColor: "text-accent",
  },
  success: {
    bg: "bg-success-bg",
    border: "border-success-border",
    icon: "✓",
    iconColor: "text-success",
  },
  warning: {
    bg: "bg-warning-bg",
    border: "border-warning-border",
    icon: "⚠",
    iconColor: "text-warning",
  },
  error: {
    bg: "bg-danger-bg",
    border: "border-danger-border",
    icon: "✕",
    iconColor: "text-danger",
  },
};
